import React, { useState, useEffect } from 'react';
import { restaurantService } from '../services/supabase';
import { getProxiedImageUrl } from '../utils/imageUtils';

const FoodTypeIndicator = ({ foodType }) => {
  const isVeg = foodType === 'veg';
  
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '16px',
      height: '16px',
      border: `2px solid ${isVeg ? '#22c55e' : '#ef4444'}`,
      borderRadius: '2px',
      marginRight: '8px',
      backgroundColor: 'white',
      flexShrink: 0
    }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: isVeg ? '#22c55e' : '#ef4444'
      }} />
    </div>
  );
};


const MenuPreview = ({ restaurant }) => {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (restaurant) {
      loadMenu();
    }
  }, [restaurant]);

  const loadMenu = async () => {
    try {
      const { data, error } = await restaurantService.getMenu(restaurant.id);
      if (error) throw error;
      
      // Filter only available items
      const availableMenu = data.map(category => ({
        ...category,
        menu_items: category.menu_items?.filter(item => item.is_available) || []
      })).filter(category => category.menu_items.length > 0);
      
      setMenu(availableMenu);
      if (availableMenu.length > 0) {
        setSelectedCategory(availableMenu[0].id);
      }
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #FF4444',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (menu.length === 0) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '30px 20px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '60px 40px',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📋</div>
            <h2 style={{ color: '#666', margin: '0 0 15px 0', fontSize: '1.5rem' }}>
              No Available Menu Items
            </h2>
            <p style={{ color: '#888', margin: '0', fontSize: '1rem' }}>
              Add menu items and mark them as available to see the customer preview
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px 20px 0 0',
          padding: '30px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
          color: 'white'
        }}>
          <h1 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '2.5rem', 
            fontWeight: '700'
          }}>
            {restaurant.name}
          </h1>
          <p style={{ margin: '0', fontSize: '1.1rem', opacity: '0.9' }}>
            Menu Preview - Customer View
          </p>
        </div>

        {/* Category Navigation */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '10px',
          overflowX: 'auto',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          {menu.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                background: selectedCategory === category.id 
                  ? 'linear-gradient(135deg, #FF4444, #FF6B00)'
                  : 'transparent',
                color: selectedCategory === category.id ? 'white' : '#666',
                border: selectedCategory === category.id ? 'none' : '2px solid #e5e7eb',
                padding: '12px 20px',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content'
              }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Menu Items */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0 0 20px 20px',
          padding: '30px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
        }}>
          {menu
            .filter(category => category.id === selectedCategory)
            .map(category => (
              <div key={category.id}>
                <h2 style={{
                  margin: '0 0 30px 0',
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#333',
                  textAlign: 'center',
                  paddingBottom: '15px',
                  borderBottom: '3px solid #FF6B00'
                }}>
                  {category.name}
                </h2>

                <div style={{ display: 'grid', gap: '25px' }}>
                  {category.menu_items.map(item => (
                    <div key={item.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      padding: '20px',
                      backgroundColor: '#fefefe',
                      borderRadius: '15px',
                      border: '2px solid #f1f3f4',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                    }}>
                      
                      <div style={{ display: 'flex', gap: '20px', flex: 1 }}>
  {item.image_url && (
    <img
      src={getProxiedImageUrl(item.image_url)}
      alt={item.name}
      style={{
        width: '100px',
        height: '100px',
        objectFit: 'cover',
        borderRadius: '12px',
        border: '3px solid #f1f3f4'
      }}
    />
  )}
  
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <FoodTypeIndicator foodType={item.food_type || 'veg'} />
                            <h3 style={{
                              margin: '0',
                              fontSize: '1.4rem',
                              fontWeight: '700',
                              color: '#333'
                            }}>
                              {item.name}
                            </h3>
                          </div>
                          
                          {item.description && (
                            <p style={{
                              margin: '0 0 15px 0',
                              color: '#666',
                              fontSize: '1rem',
                              lineHeight: '1.5'
                            }}>
                              {item.description}
                            </p>
                          )}
                          
                          <div style={{
                            display: 'inline-block',
                            background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}>
                            Available Now
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right', marginLeft: '20px' }}>
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: '700',
                          color: '#FF6B00',
                          marginBottom: '5px'
                        }}>
                          {item.currency || '$'}{item.price}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '30px',
          padding: '20px',
          color: '#666'
        }}>
          <p style={{ margin: '0', fontSize: '0.9rem' }}>
            This is how your menu appears to customers when they scan your QR codes
          </p>
        </div>
      </div>
    </div>
  );
};

export default MenuPreview;